/**
 * Type Firestore pour le module Partenaires (internal partners).
 * Stocké sous users/{uid}.partnerSubscription.
 *
 * Écriture exclusivement serveur (webhook Stripe + Cloud Functions).
 * Lecture autorisée uniquement à l'utilisateur lui-même + admin/staff.
 */

import type { Timestamp } from "firebase/firestore";

export type PartnerAddonSlug =
  | "business_law"
  | "immigration_law"
  | "notary"
  | "cpa"
  | "tax_advisor";

export const PARTNER_ADDON_SLUGS: readonly PartnerAddonSlug[] = [
  "business_law",
  "immigration_law",
  "notary",
  "cpa",
  "tax_advisor",
] as const;

export const PARTNER_ADDON_LABELS: Record<PartnerAddonSlug, string> = {
  business_law: "Business Law",
  immigration_law: "Immigration Law",
  notary: "Notary",
  cpa: "CPA",
  tax_advisor: "Tax Advisor",
};

export const PARTNER_BASE_AMOUNT_USD = 100;
export const PARTNER_ADDON_AMOUNT_USD = 35;

export type PartnerSubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "unpaid"
  | "paused";

export interface PartnerSubscriptionDoc {
  /** Base partenaire (100 $/mois) active. */
  baseActive: boolean;
  /** Add-ons actifs. Synchronisé depuis Stripe via webhook. */
  addons: PartnerAddonSlug[];
  /** ID de la subscription Stripe sous-jacente. */
  stripeSubscriptionId: string;
  /** ID du customer Stripe. */
  stripeCustomerId: string;
  /** Total mensuel calculé serveur en USD (base + add-ons × 35). */
  monthlyTotal: number;
  /** Statut Stripe normalisé. */
  status: PartnerSubscriptionStatus;
  /** Fin du cycle de facturation courant. */
  currentPeriodEnd: Timestamp;
  /** Si true, sera annulée à la fin du cycle. */
  cancelAtPeriodEnd: boolean;
  /** Date de mise à jour serveur. */
  updatedAt: Timestamp;
}

/** Calcule le total mensuel en USD à partir du nombre d'add-ons. */
export function computePartnerMonthlyTotal(addonsCount: number): number {
  return PARTNER_BASE_AMOUNT_USD + addonsCount * PARTNER_ADDON_AMOUNT_USD;
}
